import { l as lodashExports } from '../_virtual/lodash.js';
import '../node_modules/.pnpm/slate-vue3@0.13.2_vue@3.5.31_typescript@5.9.3_/node_modules/slate-vue3/dist/create-editor-z7CFqjGE.js';
import { E as Editor, N as Node, d as Transforms, R as Range } from '../node_modules/.pnpm/slate-vue3@0.13.2_vue@3.5.31_typescript@5.9.3_/node_modules/slate-vue3/dist/index-B_A1dZu-.js';
import { NodeUtils } from './node-utils.js';

const EditorUtils = {
  findNode(editor, at, options) {
    try {
      const nodeEntry = editor.node(at, options);
      return nodeEntry ? nodeEntry[0] : null;
    } catch (_) {
      return null;
    }
  },
  findNodeEntries(editor, node) {
    let allNodeEntries = Array.from(Node.nodes(editor));
    if (node && node !== editor) {
      const nodes = Array.from(Node.nodes(node)).map((nodeEntry) => {
        return nodeEntry[0];
      });
      allNodeEntries = allNodeEntries.filter((nodeEntry) => {
        return nodes.includes(nodeEntry[0]);
      });
    }
    return allNodeEntries;
  },
  findElementEntries(editor, node) {
    let allElementEntries = Array.from(Node.elements(editor));
    if (node && node !== editor) {
      const elements = Array.from(Node.elements(node)).map((elementEntry) => {
        return elementEntry[0];
      });
      allElementEntries = allElementEntries.filter((elementEntry) => {
        return elements.includes(elementEntry[0]);
      });
    }
    return allElementEntries;
  },
  findTextEntries(editor, node) {
    let allTextEntries = Array.from(Node.texts(editor));
    if (node && node !== editor) {
      const texts = Array.from(Node.texts(node)).map((textEntry) => {
        return textEntry[0];
      });
      allTextEntries = allTextEntries.filter((textEntry) => {
        return texts.includes(textEntry[0]);
      });
    }
    return allTextEntries;
  },
  findPath(editor, node) {
    for (const [n, path] of this.findNodeEntries(editor)) {
      if (n === node) {
        return path;
      }
    }
    return null;
  },
  findParentNodeEntry(editor, at) {
    return Editor.parent(editor, at);
  },
  findParent(editor, at) {
    return this.findParentNodeEntry(editor, at)[0];
  },
  findParentByNode(editor, node) {
    const path = this.findPath(editor, node);
    if (path) {
      return this.findParent(editor, path);
    } else {
      return null;
    }
  },
  findParentPath(editor, at) {
    return this.findParentNodeEntry(editor, at)[1];
  },
  getText(editor, at) {
    return Editor.string(editor, at);
  },
  getLength(editor, at) {
    return this.getText(editor, at).replace(/\r|\n|(\r\n)/g, "").length;
  },
  replaceNode(editor, originalNode, targetNode) {
    const path = this.findPath(editor, originalNode);
    if (path) {
      Transforms.setNodes(editor, targetNode, {
        at: path
      });
      if (NodeUtils.isElementNode(originalNode) && NodeUtils.isElementNode(targetNode) && !editor.isVoid(originalNode)) {
        if (targetNode.children) {
          this.removeChildrenByNode(editor, originalNode);
          this.insertChildren(
            editor,
            originalNode,
            targetNode.children
          );
        }
      }
    }
  },
  replaceChildren(editor, parentNode, children) {
    if (children.length > 0) {
      const parentPath = this.findPath(editor, parentNode);
      if (parentPath) {
        Editor.withoutNormalizing(editor, () => {
          const newParentNode = Object.assign({}, parentNode, {
            children
          });
          Transforms.removeNodes(editor, {
            at: parentPath
          });
          Transforms.insertNodes(editor, newParentNode, {
            at: parentPath
          });
        });
      }
    }
  },
  unsetChildren(editor, node) {
    this.replaceChildren(editor, node, NodeUtils.getUnsetChildren(node));
  },
  insertNode(editor, node, at) {
    if (at) {
      Transforms.insertNodes(editor, node, {
        at
      });
    } else {
      Editor.insertNode(editor, node);
    }
  },
  insertChildNode(editor, parentNode, node) {
    if (NodeUtils.isElementNode(parentNode)) {
      const parentNodePath = this.findPath(editor, parentNode);
      if (parentNodePath) {
        const childNodePath = [
          ...parentNodePath,
          parentNode.children.length
        ];
        this.insertNode(editor, node, childNodePath);
      }
    }
  },
  insertChildren(editor, parentNode, children) {
    lodashExports.cloneDeep(children).forEach((child) => {
      this.insertChildNode(editor, parentNode, child);
    });
  },
  select(editor, target) {
    Transforms.select(editor, target);
  },
  selectByNode(editor, node) {
    const path = this.findPath(editor, node);
    if (path) {
      this.select(editor, path);
    }
  },
  remove(editor, target) {
    Transforms.removeNodes(editor, {
      at: target
    });
  },
  removeByNode(editor, node) {
    const path = this.findPath(editor, node);
    if (path) {
      this.remove(editor, path);
    }
  },
  removeChildrenByNode(editor, node) {
    Editor.withoutNormalizing(editor, () => {
      if (NodeUtils.isElementNode(node)) {
        const children = node.children;
        while (children.length > 0) {
          this.removeByNode(editor, children[0]);
        }
      }
    });
  },
  unwrap(editor, target) {
    Transforms.unwrapNodes(editor, {
      at: target
    });
  },
  unwrapByNode(editor, node) {
    const path = this.findPath(editor, node);
    if (path) {
      this.unwrap(editor, path);
    }
  },
  unwrapAllByNode(editor, node, removeVoidNode = false) {
    Editor.withoutNormalizing(editor, () => {
      const elements = this.findElementEntries(editor, node).reverse();
      for (const [element, path] of elements) {
        if (removeVoidNode && editor.isVoid(element)) {
          this.remove(editor, path);
        } else {
          this.unwrap(editor, path);
        }
      }
    });
  },
  unwrapChildrenByNode(editor, node, removeVoidNode = false) {
    Editor.withoutNormalizing(editor, () => {
      if (NodeUtils.isElementNode(node)) {
        node.children.forEach((child) => {
          this.unwrapAllByNode(editor, child, removeVoidNode);
        });
      }
    });
  },
  trimSelection(editor) {
    const { selection } = editor;
    if (selection) {
      let [start, end] = Range.edges(selection);
      const wordRange = Editor.range(editor, start, end);
      const wordText = Editor.string(editor, wordRange);
      const trimStartedText = wordText.trimStart();
      if (trimStartedText !== wordText) {
        const difference = wordText.length - trimStartedText.length;
        start = { ...start, offset: start.offset + difference };
      }
      const trimEndedText = wordText.trimEnd();
      if (trimEndedText !== wordText) {
        const difference = wordText.length - trimEndedText.length;
        end = { ...end, offset: end.offset - difference };
      }
      const newSelection = { ...selection, anchor: start, focus: end };
      this.select(editor, newSelection);
    }
  },
  insertSpace(editor, range) {
    Editor.withoutNormalizing(editor, () => {
      const startPoint = Editor.start(editor, range);
      const endPoint = Editor.end(editor, range);
      Transforms.insertText(editor, " ", { at: startPoint });
      Transforms.insertText(editor, " ", {
        at: { path: endPoint.path, offset: endPoint.offset + 1 }
      });
      Transforms.select(editor, {
        anchor: { path: startPoint.path, offset: startPoint.offset + 1 },
        focus: { path: endPoint.path, offset: endPoint.offset + 1 }
      });
    });
  },
  removeSpace(editor, node) {
    Editor.withoutNormalizing(editor, () => {
      const path = this.findPath(editor, node);
      if (path) {
        const startPoint = Editor.before(editor, path);
        const endPoint = Editor.after(editor, path);
        if (startPoint && endPoint) {
          const startRange = {
            anchor: { path: startPoint.path, offset: startPoint.offset - 1 },
            focus: { path: startPoint.path, offset: startPoint.offset }
          };
          const endRange = {
            anchor: { path: endPoint.path, offset: endPoint.offset },
            focus: { path: endPoint.path, offset: endPoint.offset + 1 }
          };
          if (Editor.string(editor, startRange) === " ") {
            Transforms.delete(editor, { at: startRange });
          }
          if (Editor.string(editor, endRange) === " ") {
            Transforms.delete(editor, { at: endRange });
          }
        }
      }
    });
  },
  findTopNode(editor, node) {
    const path = this.findPath(editor, node);
    if (path) {
      const topPath = [path[0]];
      return Node.get(editor, topPath);
    } else {
      return null;
    }
  },
  findSelectedNodeByType(editor, type) {
    const [nodeEntry] = Editor.nodes(editor, {
      match: (node) => NodeUtils.checkNodeType(node, type),
      universal: true
    });
    return nodeEntry ? nodeEntry[0] : null;
  },
  findSelectedTextNode(editor) {
    const [nodeEntry] = Editor.nodes(editor, {
      match: (node) => NodeUtils.isTextNode(node),
      universal: true
    });
    return nodeEntry ? nodeEntry[0] : null;
  }
};

export { EditorUtils };
//# sourceMappingURL=editor-utils.js.map
